// component copied from react-otp-input v2.4.0 after problems with upgrading to v3 that broke how we use component
// component required upgrade as it had hardcoded peerDependency on react 16 that caused errors on installation
import type { ChangeEventHandler } from "react";
import React, { Component, PureComponent } from "react";

import { defaultTranslations, getTranslationValue, TranslationsContext } from "@jti/fe-common/translation";

// Doesn't really check if it's a style Object
// Basic implementation to check if it's not a string
// of classNames and is an Object
const isStyleObject = (obj?: string | React.CSSProperties): obj is React.CSSProperties => typeof obj === "object";

export type GetInputAriaLabelParams = {
  index: number;
  isInputNum: boolean;
};

export type GetInputAriaLabel = (params: GetInputAriaLabelParams) => string;

const INPUT_ARIA_LABEL_TRANSLATION_KEYS = {
  character: "libs.ui.otp_input.character_aria_label",
  digit: "libs.ui.otp_input.digit_aria_label",
} as const;

const DEFAULT_TRANSLATIONS = defaultTranslations as Record<string, string>;

type SingleOtpInputProps = {
  "data-testid"?: string;
  className?: string;
  index: number;
  focus: boolean;
  shouldAutoFocus: boolean;
  separator?: string;
  isLastChild: boolean;
  inputStyle?: React.CSSProperties | string;
  isInputSecure: boolean;
  isDisabled: boolean;
  hasErrored: boolean;
  errorStyle?: React.CSSProperties | string;
  focusStyle?: React.CSSProperties | string;
  disabledStyle?: React.CSSProperties | string;
  isInputNum: boolean;
  value: string;
  placeholder?: string;
  autocompleteType: string;
  getInputAriaLabel: GetInputAriaLabel;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onPaste: React.ClipboardEventHandler<HTMLInputElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onInput: React.FormEventHandler<HTMLInputElement>;
  onFocus: React.FocusEventHandler<HTMLInputElement>;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
};

class SingleOtpInput extends PureComponent<SingleOtpInputProps> {
  input: React.RefObject<HTMLInputElement | null>;

  constructor(props: SingleOtpInputProps) {
    super(props);
    this.input = React.createRef();
  }

  // Focus on first render
  // Only when shouldAutoFocus is true
  componentDidMount() {
    const { focus, shouldAutoFocus } = this.props;
    const { current: inputEl } = this.input;

    if (inputEl && focus && shouldAutoFocus) {
      inputEl.focus();
    }
  }

  componentDidUpdate(prevProps: SingleOtpInputProps) {
    const { focus } = this.props;
    const { current: inputEl } = this.input;

    // Check if focusedInput changed
    // Prevent calling function if input already in focus
    if (prevProps.focus !== focus && inputEl && focus) {
      inputEl.focus();
      inputEl.select();
    }
  }

  getClasses = (...classes: (string | React.CSSProperties | undefined | false)[]) =>
    classes.filter((c) => !!c && !isStyleObject(c)).join(" ");

  getType = () => {
    const { isInputSecure, isInputNum } = this.props;

    if (isInputSecure) {
      return "password";
    }

    if (isInputNum) {
      return "tel";
    }

    return "text";
  };

  render() {
    const {
      placeholder,
      separator,
      isLastChild,
      inputStyle,
      focus,
      isDisabled,
      hasErrored,
      errorStyle,
      focusStyle,
      disabledStyle,
      isInputNum,
      autocompleteType,
      index,
      value,
      className,
      getInputAriaLabel,
      onInput,
      onBlur,
      onFocus,
      onKeyDown,
      onPaste,
      onChange,
      "data-testid": dataTestId,
    } = this.props;

    return (
      <div className={className} style={{ display: "flex", alignItems: "center" }}>
        <input
          aria-label={getInputAriaLabel({ index, isInputNum })}
          autoComplete={autocompleteType}
          style={Object.assign(
            { width: "1em", textAlign: "center" },
            isStyleObject(inputStyle) && inputStyle,
            focus && isStyleObject(focusStyle) && focusStyle,
            isDisabled && isStyleObject(disabledStyle) && disabledStyle,
            hasErrored && isStyleObject(errorStyle) && errorStyle
          )}
          placeholder={placeholder}
          className={this.getClasses(
            inputStyle,
            focus && focusStyle,
            isDisabled && disabledStyle,
            hasErrored && errorStyle
          )}
          type={this.getType()}
          ref={this.input}
          disabled={isDisabled}
          value={value ? value : ""}
          onFocus={onFocus}
          onBlur={onBlur}
          onInput={onInput}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onChange={onChange}
          data-testid={dataTestId}
          inputMode={isInputNum ? "numeric" : "text"}
        />
        {!isLastChild && separator}
      </div>
    );
  }
}

export type OtpInputProps = {
  value?: string;
  separator?: string;
  className?: string;
  isInputSecure?: boolean;
  onChange?: (value: string) => void;
  numInputs?: number;
  placeholder?: string;
  isDisabled?: boolean;
  hasErrored?: boolean;
  shouldAutoFocus?: boolean;
  isInputNum?: boolean;
  autocompleteType?: string;
  getInputAriaLabel?: GetInputAriaLabel;
  containerStyle?: React.CSSProperties | string;
  inputStyle?: React.CSSProperties | string;
  focusStyle?: React.CSSProperties | string;
  errorStyle?: React.CSSProperties | string;
  disabledStyle?: React.CSSProperties | string;
  "data-testid"?: string;
  "data-cy"?: string;
};

const defaultNumInputs = 4;
export class OtpInput extends Component<OtpInputProps> {
  static contextType = TranslationsContext;

  declare context: React.ContextType<typeof TranslationsContext>;

  static defaultProps = {
    numInputs: defaultNumInputs,
    onChange: (otp: string): void => console.log(otp),
    isDisabled: false,
    shouldAutoFocus: false,
    value: "",
    isInputSecure: false,
  };

  state = {
    activeInput: 0,
  };

  getDefaultInputAriaLabel: GetInputAriaLabel = ({ index, isInputNum }) => {
    const translationKey = isInputNum
      ? INPUT_ARIA_LABEL_TRANSLATION_KEYS.digit
      : INPUT_ARIA_LABEL_TRANSLATION_KEYS.character;
    const dictionary = this.context?.[translationKey] === undefined ? DEFAULT_TRANSLATIONS : this.context;

    return getTranslationValue(dictionary)(translationKey, [index + 1]);
  };

  getOtpValue = (): string[] => (this.props.value ? this.props.value.toString().split("") : []);

  getPlaceholderValue = (): string | undefined => {
    const { placeholder, numInputs } = this.props;

    if (typeof placeholder === "string") {
      if (placeholder.length === numInputs) {
        return placeholder;
      }

      if (placeholder.length > 0) {
        console.error("Length of the placeholder should be equal to the number of inputs.");
      }
    }
  };

  // Helper to return OTP from input
  handleOtpChange = (otp: string[]): void => {
    const { onChange } = this.props;
    const otpValue = otp.join("");

    onChange?.(otpValue);
  };

  isInputValueHasValidType = (value: string): boolean =>
    this.props.isInputNum ? !isNaN(parseInt(value, 10)) : typeof value === "string";

  isSingleInputValueValid = (value: string): boolean =>
    this.isInputValueHasValidType(value) && value.trim().length === 1;

  // Focus on input by index
  focusInput = (input: number): void => {
    const { numInputs = defaultNumInputs } = this.props;
    const activeInput = Math.max(Math.min(numInputs - 1, input), 0);

    this.setState({ activeInput });
  };

  // Focus on next input
  focusNextInput = (): void => {
    const { activeInput } = this.state;
    this.focusInput(activeInput + 1);
  };

  // Focus on previous input
  focusPrevInput = (): void => {
    const { activeInput } = this.state;
    this.focusInput(activeInput - 1);
  };

  // Change OTP value at focused input
  changeCodeAtFocus = (value: string): void => {
    const { activeInput } = this.state;
    const otp = this.getOtpValue();
    otp[activeInput] = value[0];

    this.handleOtpChange(otp);
  };

  breakAndPasteOtp = (otpCode: string) => {
    const otp = this.getOtpValue();
    const { numInputs = defaultNumInputs } = this.props;
    const { activeInput } = this.state;
    let nextActiveInput = activeInput;

    // Get pastedData in an array of max size (num of inputs - current position)
    const pastedData = otpCode.slice(0, numInputs - activeInput).split("");

    // Paste data from focused input onwards
    for (let pos = 0; pos < numInputs; ++pos) {
      if (pos >= activeInput && pastedData.length > 0) {
        otp[pos] = pastedData.shift() || "";
        nextActiveInput++;
      }
    }

    this.setState({ activeInput: nextActiveInput }, () => {
      this.focusInput(nextActiveInput);
      this.handleOtpChange(otp);
    });
  };

  // Handle pasted OTP
  handleOnPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();

    const { isDisabled } = this.props;

    if (isDisabled) {
      return;
    }

    // Get pastedData
    const pastedData = e.clipboardData.getData("text/plain");

    this.breakAndPasteOtp(pastedData);
  };

  handleOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;

    if (this.isSingleInputValueValid(value)) {
      this.changeCodeAtFocus(value);
    }
  };

  // Handle cases of backspace, delete, left arrow, right arrow, space
  handleOnKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.code === "Backspace" || e.key === "Backspace") {
      e.preventDefault();
      this.changeCodeAtFocus("");
      this.focusPrevInput();
    } else if (e.code === "Delete" || e.key === "Delete") {
      e.preventDefault();
      this.changeCodeAtFocus("");
    } else if (e.code === "ArrowLeft" || e.key === "ArrowLeft") {
      e.preventDefault();
      this.focusPrevInput();
    } else if (e.code === "ArrowRight" || e.key === "ArrowRight") {
      e.preventDefault();
      this.focusNextInput();
    } else if (e.key === " " || e.code === "Space") {
      e.preventDefault();
    }
  };

  // The content may not have changed, but some input took place hence change the focus
  handleOnInput = (e: React.FormEvent<HTMLInputElement>): void => {
    const { numInputs = defaultNumInputs } = this.props;

    if (e.currentTarget.value.length === numInputs && this.isInputValueHasValidType(e.currentTarget.value)) {
      this.breakAndPasteOtp(e.currentTarget.value);
    }

    if (this.isSingleInputValueValid(e.currentTarget.value)) {
      this.focusNextInput();
    } else {
      // This is a workaround for dealing with keyCode "229 Unidentified" on Android.

      if (!this.props.isInputNum) {
        const nativeEvent = e.nativeEvent;

        if (
          nativeEvent instanceof InputEvent &&
          nativeEvent.data === null &&
          nativeEvent.inputType === "deleteContentBackward"
        ) {
          e.preventDefault();
          this.changeCodeAtFocus("");
          this.focusPrevInput();
        }
      }
    }
  };

  renderInputs = (): JSX.Element[] => {
    const { activeInput } = this.state;
    const {
      numInputs = defaultNumInputs,
      inputStyle,
      focusStyle,
      separator,
      isDisabled = false,
      disabledStyle,
      hasErrored = false,
      errorStyle,
      shouldAutoFocus = false,
      isInputNum = false,
      isInputSecure = false,
      className,
      autocompleteType = "off",
      getInputAriaLabel = this.getDefaultInputAriaLabel,
    } = this.props;

    const inputs: JSX.Element[] = [];
    const otp = this.getOtpValue();
    const placeholder = this.getPlaceholderValue();
    const dataCy = this.props["data-cy"];
    const dataTestId = this.props["data-testid"];

    const isRTL = document.dir === "rtl";

    for (let i = 0; i < numInputs; i++) {
      const isLastChild = isRTL ? i === 0 : i === numInputs - 1;

      inputs.push(
        <SingleOtpInput
          placeholder={placeholder?.[i]}
          key={i}
          index={i}
          focus={activeInput === i}
          value={otp?.[i]}
          onChange={this.handleOnChange}
          onKeyDown={this.handleOnKeyDown}
          onInput={this.handleOnInput}
          onPaste={this.handleOnPaste}
          onFocus={(e) => {
            this.setState({ activeInput: i });
            e.target.select();
          }}
          onBlur={() => this.setState({ activeInput: -1 })}
          separator={separator}
          inputStyle={inputStyle}
          focusStyle={focusStyle}
          isLastChild={isLastChild}
          isDisabled={isDisabled}
          disabledStyle={disabledStyle}
          hasErrored={hasErrored}
          errorStyle={errorStyle}
          shouldAutoFocus={shouldAutoFocus}
          isInputNum={isInputNum}
          isInputSecure={isInputSecure}
          className={className}
          data-cy={dataCy && `${dataCy}-${i}`}
          data-testid={dataTestId && `${dataTestId}-${i}`}
          autocompleteType={autocompleteType}
          getInputAriaLabel={getInputAriaLabel}
        />
      );
    }

    return isRTL ? inputs.reverse() : inputs;
  };

  render(): JSX.Element {
    const { containerStyle } = this.props;

    return (
      <div
        style={Object.assign({ display: "flex" }, isStyleObject(containerStyle) ? containerStyle : {})}
        className={!isStyleObject(containerStyle) ? containerStyle : ""}
        data-testid="otpContainer"
      >
        {this.renderInputs()}
      </div>
    );
  }
}
